import React, { memo, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

/**
 * EliteContentDocument — the admin-facing renderer for generated Elite
 * Mastery markdown. Optimised for reading long technical documents:
 * centred measure, calm typography, real code blocks, scrollable tables.
 *
 * Security: generated content is untrusted. Raw HTML inside the markdown is
 * NOT rendered (no rehype-raw — react-markdown drops it), URLs are limited to
 * http(s)/mailto/relative (javascript:/data: are dropped by react-markdown's
 * default url transform and again here), images only load from http(s), and
 * every link opens with rel="noopener noreferrer". Nothing from the document
 * can execute.
 */

export const slugifyHeading = (text) =>
  String(text || '')
    .toLowerCase()
    .replace(/[`*_~]/g, '')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 80) || 'section';

/** Headings (##/###) with stable ids — the same ids the renderer assigns. */
export const extractOutline = (markdown) => {
  const out = [];
  const seen = new Map();
  let inFence = false;
  for (const raw of String(markdown || '').split('\n')) {
    const line = raw.trimEnd();
    if (/^\s*(```|~~~)/.test(line)) { inFence = !inFence; continue; }
    if (inFence) continue;
    const m = line.match(/^(#{1,3})\s+(.+?)\s*#*$/);
    if (!m) continue;
    const level = m[1].length;
    const text = m[2].replace(/[`*_~]/g, '').trim();
    let id = slugifyHeading(text);
    const n = seen.get(id) || 0;
    seen.set(id, n + 1);
    if (n > 0) id = `${id}-${n}`;
    out.push({ id, text, level });
  }
  return out;
};

const textOf = (children) => {
  const walk = (c) => {
    if (c == null || typeof c === 'boolean') return '';
    if (typeof c === 'string' || typeof c === 'number') return String(c);
    if (Array.isArray(c)) return c.map(walk).join('');
    if (c.props && c.props.children) return walk(c.props.children);
    return '';
  };
  return walk(children);
};

const safeHref = (href) => {
  const h = String(href || '').trim();
  if (!h) return null;
  if (/^(https?:|mailto:|\/|#|\.\.?\/)/i.test(h)) return h;
  return null;
};
const safeImageSrc = (src) => (/^https?:\/\//i.test(String(src || '')) ? src : null);

const CodeBlock = ({ language, value }) => (
  <div className="ecd-code group">
    <div className="ecd-code-bar">
      <span>{language || 'code'}</span>
      <button
        type="button"
        className="ecd-copy"
        onClick={() => { try { navigator.clipboard.writeText(value); } catch (_) { /* clipboard unavailable */ } }}
        aria-label="Copy code"
      >
        Copy
      </button>
    </div>
    <div className="ecd-code-scroll">
      <SyntaxHighlighter
        style={vscDarkPlus}
        language={language || 'text'}
        PreTag="div"
        customStyle={{ margin: 0, borderRadius: 0, padding: '18px 20px', fontSize: '13.5px', lineHeight: 1.65, background: '#1E1E2E' }}
        codeTagProps={{ style: { fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace' } }}
        showLineNumbers={value.split('\n').length > 4}
        lineNumberStyle={{ minWidth: '2.6em', paddingRight: '1em', color: '#585B70', textAlign: 'right', userSelect: 'none' }}
        wrapLongLines={false}
      >
        {value}
      </SyntaxHighlighter>
    </div>
  </div>
);

const EliteContentDocument = memo(function EliteContentDocument({ markdown, className = '' }) {
  const outline = useMemo(() => extractOutline(markdown), [markdown]);
  const idQueue = useMemo(() => {
    const q = new Map();
    for (const h of outline) {
      const key = `${h.level}:${h.text}`;
      if (!q.has(key)) q.set(key, []);
      q.get(key).push(h.id);
    }
    return q;
  }, [outline]);
  // consume ids in document order so duplicate headings get "-1", "-2" …
  const used = useMemo(() => new Map(), [markdown]); // eslint-disable-line react-hooks/exhaustive-deps
  const headingId = (level, children) => {
    const text = textOf(children).replace(/[`*_~]/g, '').trim();
    const key = `${level}:${text}`;
    const ids = idQueue.get(key);
    const idx = used.get(key) || 0;
    used.set(key, idx + 1);
    return (ids && ids[idx]) || slugifyHeading(text);
  };

  const components = useMemo(() => ({
    h1: ({ children }) => <h1 id={headingId(1, children)} className="ecd-h1">{children}</h1>,
    h2: ({ children }) => <h2 id={headingId(2, children)} className="ecd-h2">{children}</h2>,
    h3: ({ children }) => <h3 id={headingId(3, children)} className="ecd-h3">{children}</h3>,
    h4: ({ children }) => <h4 className="ecd-h4">{children}</h4>,
    p: ({ children }) => <p className="ecd-p">{children}</p>,
    a: ({ href, children }) => {
      const h = safeHref(href);
      if (!h) return <span className="ecd-link-disabled">{children}</span>;
      const external = /^https?:/i.test(h);
      return <a href={h} className="ecd-link" target={external ? '_blank' : undefined} rel="noopener noreferrer nofollow">{children}</a>;
    },
    img: ({ src, alt }) => {
      const s = safeImageSrc(src);
      if (!s) return <span className="ecd-img-missing">[image omitted: {alt || 'unsafe source'}]</span>;
      return <img src={s} alt={alt || ''} loading="lazy" className="ecd-img" referrerPolicy="no-referrer" />;
    },
    ul: ({ children }) => <ul className="ecd-ul">{children}</ul>,
    ol: ({ children, start }) => <ol className="ecd-ol" start={start}>{children}</ol>,
    li: ({ children }) => <li className="ecd-li">{children}</li>,
    blockquote: ({ children }) => <blockquote className="ecd-quote">{children}</blockquote>,
    hr: () => <hr className="ecd-hr" />,
    table: ({ children }) => <div className="ecd-table-wrap"><table className="ecd-table">{children}</table></div>,
    th: ({ children }) => <th className="ecd-th">{children}</th>,
    td: ({ children }) => <td className="ecd-td">{children}</td>,
    // react-markdown v9+: fenced code arrives as <pre><code class="language-x">…</code></pre>.
    // The <pre> wrapper is intercepted here so only inline code reaches `code`.
    pre: ({ children }) => {
      const child = Array.isArray(children) ? children[0] : children;
      const props = (child && child.props) || {};
      const value = textOf(props.children).replace(/\n$/, '');
      const match = /language-([\w+-]+)/.exec(props.className || '');
      return <CodeBlock language={match ? match[1] : ''} value={value} />;
    },
    code: ({ children }) => <code className="ecd-inline-code">{children}</code>,
    // raw html nodes never reach here (no rehype-raw); keep a defensive no-op
    html: () => null,
    script: () => null,
    iframe: () => null,
  }), [idQueue]); // eslint-disable-line react-hooks/exhaustive-deps

  used.clear();

  return (
    <article className={`ecd-doc ${className}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml components={components}>
        {markdown || ''}
      </ReactMarkdown>
    </article>
  );
});

/** Scoped styles — injected once; calm documentation typography. */
export const ELITE_CONTENT_DOC_CSS = `
.ecd-doc{color:#1F2937;font-family:"Plus Jakarta Sans","Inter","Segoe UI",system-ui,sans-serif;font-size:16px;line-height:1.75;word-wrap:break-word;overflow-wrap:anywhere}
.ecd-doc > :first-child{margin-top:0}
.ecd-h1{font-size:2rem;line-height:1.25;font-weight:800;color:#111827;margin:0 0 1.25rem;letter-spacing:-0.01em}
.ecd-h2{font-size:1.5rem;line-height:1.3;font-weight:800;color:#111827;margin:2.75rem 0 1rem;padding-bottom:.5rem;border-bottom:1px solid #E5E7EB;scroll-margin-top:120px}
.ecd-h3{font-size:1.2rem;line-height:1.35;font-weight:700;color:#1F2937;margin:2rem 0 .75rem;scroll-margin-top:120px}
.ecd-h4{font-size:1.02rem;font-weight:700;color:#374151;margin:1.5rem 0 .5rem}
.ecd-p{margin:0 0 1.1rem}
.ecd-link{color:#5B21B6;text-decoration:underline;text-decoration-color:rgba(91,33,182,.35);text-underline-offset:3px;font-weight:600}
.ecd-link:hover{text-decoration-color:#5B21B6}
.ecd-link-disabled{color:#6B7280;border-bottom:1px dotted #9CA3AF}
.ecd-ul,.ecd-ol{margin:0 0 1.1rem;padding-left:1.6rem}
.ecd-ul{list-style:disc}.ecd-ol{list-style:decimal}
.ecd-li{margin:.35rem 0}.ecd-li > .ecd-p{margin-bottom:.4rem}
.ecd-li > .ecd-ul,.ecd-li > .ecd-ol{margin-top:.35rem;margin-bottom:.35rem}
.ecd-quote{margin:1.25rem 0;padding:.75rem 1.1rem;border-left:4px solid #C4B5FD;background:#F9F7FF;border-radius:0 12px 12px 0;color:#4B5563}
.ecd-quote .ecd-p:last-child{margin-bottom:0}
.ecd-hr{border:0;border-top:1px solid #E5E7EB;margin:2.5rem 0}
.ecd-inline-code{font-family:"JetBrains Mono",ui-monospace,SFMono-Regular,Menlo,monospace;font-size:.875em;background:#F3F0FF;color:#5B21B6;padding:.15em .4em;border-radius:6px;border:1px solid #E9E3FF}
.ecd-code{margin:1.4rem 0;border-radius:14px;overflow:hidden;border:1px solid #2A2A3C;background:#1E1E2E;box-shadow:0 8px 24px rgba(17,24,39,.08)}
.ecd-code-bar{display:flex;align-items:center;justify-content:space-between;padding:.45rem .9rem;background:#181825;color:#A6ADC8;font-size:.7rem;font-weight:800;letter-spacing:.08em;text-transform:uppercase}
.ecd-copy{font:inherit;color:#CDD6F4;background:transparent;border:1px solid #45475A;border-radius:6px;padding:.15rem .55rem;cursor:pointer;opacity:0;transition:opacity .15s}
.ecd-code:hover .ecd-copy,.ecd-copy:focus-visible{opacity:1}
.ecd-copy:focus-visible{outline:2px solid #A78BFA;outline-offset:2px}
.ecd-code-scroll{overflow-x:auto}
.ecd-table-wrap{overflow-x:auto;margin:1.4rem 0;border:1px solid #E5E7EB;border-radius:12px}
.ecd-table{width:100%;border-collapse:collapse;font-size:.92rem;min-width:520px}
.ecd-th{background:#F9FAFB;text-align:left;font-weight:800;color:#374151;padding:.65rem .85rem;border-bottom:1px solid #E5E7EB;white-space:nowrap}
.ecd-td{padding:.6rem .85rem;border-bottom:1px solid #F3F4F6;vertical-align:top}
.ecd-table tr:last-child .ecd-td{border-bottom:0}
.ecd-img{max-width:100%;height:auto;border-radius:12px;border:1px solid #E5E7EB;margin:1rem auto;display:block}
.ecd-img-missing{color:#9CA3AF;font-size:.85rem;font-style:italic}
.ecd-doc svg{max-width:100%;height:auto}
.ecd-doc strong{font-weight:700;color:#111827}
@media (max-width:640px){.ecd-doc{font-size:15px}.ecd-h1{font-size:1.6rem}.ecd-h2{font-size:1.3rem;margin-top:2rem}.ecd-h3{font-size:1.1rem}}
`;

export default EliteContentDocument;
